import { createContext, createElement, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

import { createHubFactory } from '../realtime/hubFactory';
import type { PortalHubConnection } from '../realtime/hubFactory';
import type { CallNotificationItem, Incident, IncidentId, PortalHubClientEvents } from '../realtime/contracts';
import { PortalClient } from './portalClient';
import { IncidentStore } from './incidentStore';
import type { IncidentsSnapshot } from './incidentStore';
import { CallStore } from './callStore';
import type { CallsSnapshot } from './callStore';
import { ConnectionStateTracker } from './connectionStateTracker';
import type { RealtimeConnectionState } from './connectionStateTracker';
import type { IncidentCall } from './callProjection';

const PortalClientContext = createContext<PortalClient | null>(null);

export type PortalClientProviderProps = {
  emsServerUrl: string;
  accessTokenFactory: () => Promise<string>;
  onError?: (message: string, error: unknown) => void;
  children: ReactNode;
};

export function PortalClientProvider({
  emsServerUrl,
  accessTokenFactory,
  onError,
  children,
}: PortalClientProviderProps) {
  const [client, setClient] = useState<PortalClient | null>(null);

  const dependencies = useRef({ accessTokenFactory, onError });
  useEffect(() => {
    dependencies.current = { accessTokenFactory, onError };
  });

  useEffect(() => {
    const hub = createHubFactory({
      emsServerUrl,
      accessTokenFactory: () => dependencies.current.accessTokenFactory(),
    }).createPortalHubConnection();

    const created = new PortalClient({
      hub,
      onError: (message, error) => dependencies.current.onError?.(message, error),
    });

    setClient(created);

    return () => {
      setClient(null);
      created.dispose();
    };
  }, [emsServerUrl]);

  return createElement(PortalClientContext.Provider, { value: client }, children);
}

export function usePortalClient(): PortalClient | null {
  return useContext(PortalClientContext);
}

function useStoreSnapshot<TSnapshot>(
  store: { subscribe: (listener: () => void) => () => void; getSnapshot: () => TSnapshot } | undefined,
  fallback: TSnapshot,
): TSnapshot {
  return useSyncExternalStore(
    (listener) => store?.subscribe(listener) ?? (() => undefined),
    () => store?.getSnapshot() ?? fallback,
    () => store?.getSnapshot() ?? fallback,
  );
}

export function useIncidents(): IncidentsSnapshot {
  return useStoreSnapshot(usePortalClient()?.incidents, IncidentStore.emptySnapshot);
}

export function useCalls(): CallsSnapshot {
  return useStoreSnapshot(usePortalClient()?.calls, CallStore.emptySnapshot);
}

export function useRealtimeConnection(): RealtimeConnectionState {
  return useStoreSnapshot(usePortalClient()?.connection, ConnectionStateTracker.connectingSnapshot);
}

export function useIncident(incidentId: IncidentId | null): Incident | null {
  const { incidentsById } = useIncidents();

  return incidentId === null ? null : (incidentsById.get(incidentId) ?? null);
}

export type UseIncidentStreamsResult = {
  isWatching: boolean;
  error: unknown;
};

export function useIncidentStreams(incidentId: IncidentId | null): UseIncidentStreamsResult {
  const client = usePortalClient();
  const [isWatching, setIsWatching] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (client === null || incidentId === null) {
      return;
    }

    let abandoned = false;
    setIsWatching(false);
    setError(null);

    void client.watchIncident(incidentId).then(
      () => {
        if (!abandoned) {
          setIsWatching(true);
        }
      },
      (watchError: unknown) => {
        if (!abandoned) {
          setError(watchError);
        }
      },
    );

    return () => {
      abandoned = true;
      setIsWatching(false);
      client.unwatchIncident(incidentId);
    };
  }, [client, incidentId]);

  return { isWatching, error };
}

export type UseIncidentCallResult = {
  call: IncidentCall | null;
  isLoading: boolean;
  error: unknown;
};

export function useIncidentCall(incidentId: IncidentId | null): UseIncidentCallResult {
  const client = usePortalClient();
  const { callsByIncidentId } = useCalls();
  const { isWatching, error: watchError } = useIncidentStreams(incidentId);
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState<unknown>(null);

  useEffect(() => {
    if (client === null || incidentId === null || !isWatching) {
      return;
    }

    let abandoned = false;
    setIsReading(true);
    setReadError(null);

    void client.loadIncidentCall(incidentId).then(
      () => {
        if (!abandoned) {
          setIsReading(false);
        }
      },
      (error: unknown) => {
        if (!abandoned) {
          setReadError(error);
          setIsReading(false);
        }
      },
    );

    return () => {
      abandoned = true;
    };
  }, [client, incidentId, isWatching]);

  return {
    call: incidentId === null ? null : (callsByIncidentId.get(incidentId) ?? null),
    isLoading: watchError === null && (!isWatching || isReading),
    error: watchError ?? readError,
  };
}

type IncidentScopedEvent = {
  [TEventName in keyof PortalHubClientEvents]: Parameters<PortalHubClientEvents[TEventName]>[0] extends IncidentId
    ? TEventName
    : never;
}[keyof PortalHubClientEvents];

type WithoutIncidentId<TParameters extends unknown[]> = TParameters extends [IncidentId, ...infer TRest]
  ? TRest
  : never;

export function useIncidentHubEvent<TEventName extends IncidentScopedEvent>(
  incidentId: IncidentId | null,
  eventName: TEventName,
  handler: (...args: WithoutIncidentId<Parameters<PortalHubClientEvents[TEventName]>>) => void,
): void {
  const hub = usePortalHub();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    if (hub === null || incidentId === null) {
      return;
    }

    return hub.on(eventName, ((eventIncidentId: IncidentId, ...args: unknown[]) => {
      if (eventIncidentId === incidentId) {
        (handlerRef.current as (...handlerArgs: unknown[]) => void)(...args);
      }
    }) as PortalHubClientEvents[TEventName]);
  }, [hub, incidentId, eventName]);
}

export function usePortalHub(): PortalHubConnection | null {
  return usePortalClient()?.hubConnection ?? null;
}

export type { CallNotificationItem };
